import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, Sparkles } from "lucide-react";

/**
 * BreakCard — Integration Pause (iter-36)
 *
 * Shown after every 5-message cycle (blueberry award) and after tier graduation.
 * Features:
 *  - The seeker's DISTILLED REALIZATION (LLM-generated) for them to meditate on
 *  - A live 1-hour countdown until the integration gate lifts
 *  - The "I've Returned" button stays disabled until the gate has lifted
 *  - If the user tried to chat too early (server returned 423), we show their
 *    nudge message inline instead of letting the chat input look broken
 *
 * Props:
 *   blueberries          — int, "X of 5" badge
 *   breakUntil           — ISO datetime string (server-side wall clock)
 *   breakRealization     — string, distilled summary; may be empty (LLM failures)
 *   earlyReturnMessage   — {message, realization_summary, break_until} | null —
 *                          surfaced when /api/chat 423s; otherwise null
 *   handleReturn         — () => void, lifts the local break state once gate is open
 */
const BreakCard = ({ blueberries, breakUntil, breakRealization, earlyReturnMessage, handleReturn }) => {
  // Re-tick once per second so the countdown actually counts. We do it locally
  // (no server polling) because the source of truth is the server's `breakUntil`
  // ISO — we just compare it to local Date.now() each second.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const breakUntilMs = breakUntil ? Date.parse(breakUntil) : null;
  const secondsLeft = breakUntilMs ? Math.max(0, Math.floor((breakUntilMs - now) / 1000)) : 0;
  const gateLifted = !breakUntil || secondsLeft <= 0;

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const countdown = `${mins}:${String(secs).padStart(2, "0")}`;

  // The earlyReturnMessage may carry its own realization fallback; prefer the
  // context value when present so the card stays consistent after a refresh.
  const realization = breakRealization || earlyReturnMessage?.realization_summary || "";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-5 rounded-2xl"
      style={{
        background: "linear-gradient(135deg, #F0E8F8 0%, #E8DEF5 100%)",
        border: "1px solid #D8CCE8",
      }}
      data-testid="break-card"
    >
      <div className="text-center mb-4">
        <div className="text-3xl mb-2" aria-hidden>&#x1F4AB;</div>
        <div className="text-sm font-bold" style={{ color: "#4F378B", fontFamily: "'Fredoka', sans-serif" }}>
          Time to Integrate
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] mt-1" style={{ color: "#7B5EA7" }}>
          {blueberries} of 5 blueberries earned
        </div>
      </div>

      {/* Distilled realization — the thing they meditate on */}
      {realization && (
        <div
          className="rounded-xl p-3 mb-4"
          style={{ background: "rgba(255,255,255,0.55)", border: "1px solid rgba(212,165,116,0.30)" }}
          data-testid="break-realization-summary"
        >
          <div className="flex items-center gap-1 mb-1.5">
            <Sparkles size={11} style={{ color: "#D4A574" }} />
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#D4A574" }}>
              Your Realization
            </span>
          </div>
          <p className="text-xs italic leading-relaxed" style={{ color: "#3D2860", fontFamily: "'Nunito', sans-serif" }}>
            {realization}
          </p>
        </div>
      )}

      {/* Early-return nudge — only shows if the server bounced them with 423 */}
      {earlyReturnMessage && !gateLifted && (
        <div
          className="rounded-xl p-3 mb-4 text-center"
          style={{ background: "rgba(212,165,116,0.18)", border: "1px solid rgba(212,165,116,0.45)" }}
          data-testid="break-early-return-nudge"
        >
          <p className="text-xs italic leading-relaxed" style={{ color: "#5C3D14" }}>
            {earlyReturnMessage.message}
          </p>
        </div>
      )}

      {/* Countdown + return button */}
      <div className="flex flex-col items-center gap-2">
        {!gateLifted ? (
          <>
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: "rgba(79,55,139,0.10)", color: "#4F378B" }}
              data-testid="break-countdown"
            >
              <Clock size={12} />
              <span>integration gate lifts in {countdown}</span>
            </div>
            <button
              type="button"
              disabled
              className="px-6 py-2 rounded-full text-sm font-semibold opacity-60 cursor-not-allowed"
              style={{ background: "rgba(79,55,139,0.35)", color: "#FFF" }}
              data-testid="return-from-break-locked"
              aria-disabled="true"
            >
              Sit with it a little longer…
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={handleReturn}
            className="px-6 py-2 rounded-full text-sm font-semibold text-white"
            style={{ background: "linear-gradient(135deg, #4F378B, #7B5EA7)" }}
            data-testid="return-from-break"
          >
            I've Returned with New Understanding
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default BreakCard;
